import React from 'react';
import { MONO_BADGE } from './typeScale';

/**
 * A mono status pill: a live/held state, a score, a count.
 *
 * <h3>Why this is a component</h3>
 *
 * <p>Five surfaces had each written the same pill by hand — {@code className="p7-mono"} plus
 * {@code fontSize:'11px', fontWeight:600, borderRadius:'999px'} and a tone's background and ink,
 * with the padding differing between them by a pixel in each direction because they were typed
 * separately. Five copies of one pill is five places for it to drift, and the hand-typed 11px
 * was one of the sizes that made the surfaces look unrelated.</p>
 *
 * <p>The tone names are the platform's own — {@code allow}, {@code hold}, {@code deny},
 * {@code accent} — so the colour is read from the token for that tone rather than chosen here.
 * An unknown tone falls back to the neutral line colour rather than throwing: a pill is never
 * worth failing a screen over.</p>
 *
 * @param {string} tone   allow | hold | deny | accent | neutral
 * @param {Object} style  placement only — where the pill sits in its row
 */
export default function Pill({ tone = 'neutral', title, style, children }) {
  const t = ['allow', 'hold', 'deny', 'accent'].includes(tone) ? tone : null;
  return (
    <span className="p7-mono"
          title={title}
          style={{
            ...MONO_BADGE,
            padding: '2px 8px',
            borderRadius: 'var(--p7-radius-pill)',
            whiteSpace: 'nowrap',
            background: t ? `var(--p7-${t}-bg)` : 'var(--p7-line-soft)',
            color: t ? `var(--p7-${t}-ink, var(--p7-${t}))` : 'var(--p7-ink-strong)',
            ...style,
          }}>
      {children}
    </span>
  );
}
